import { useState, useRef } from "react";
import { toast } from "sonner";
import { X, Upload, Trash2, Plus, ChevronLeft, ChevronRight, FileText } from "lucide-react";
import { api, mediaUrl, CATEGORIES } from "@/lib/api";

const empty = {
  title: "", client: "", category: "Branding", categories: ["Branding"], year: `${new Date().getFullYear()}`,
  cover: "", tagline: "", featured: false, problem: "", approach: "",
  deliverables: [], results: [], gallery: [], gallery_layout: "grid", order: 0,
};

const field = "w-full bg-transparent border border-border rounded-lg focus:border-accent outline-none px-4 py-3 text-ink transition-colors";
const lbl = "text-xs uppercase tracking-widest text-muted2 block mb-2";

export default function ProjectForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(
    initial
      ? { ...empty, ...initial, categories: (initial.categories && initial.categories.length) ? initial.categories : (initial.category ? [initial.category] : []) }
      : empty
  );
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const coverRef = useRef();
  const galleryRef = useRef();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setList = (k, str) => set(k, str.split("\n").map((s) => s.trim()).filter(Boolean));

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  };

  const onCover = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try { const d = await uploadFile(file); set("cover", d.url); toast.success("Cover uploaded"); }
    catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const onGallery = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) { const d = await uploadFile(f); uploaded.push({ type: d.type, url: d.url, caption: "" }); }
      set("gallery", [...form.gallery, ...uploaded]);
      toast.success(`${uploaded.length} file(s) added`);
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const removeMedia = (i) => set("gallery", form.gallery.filter((_, idx) => idx !== i));
  const setCaption = (i, val) => set("gallery", form.gallery.map((m, idx) => (idx === i ? { ...m, caption: val } : m)));
  const setField = (i, key, val) => set("gallery", form.gallery.map((m, idx) => (idx === i ? { ...m, [key]: val } : m)));
  const moveMedia = (i, dir) => {
    const t = i + dir;
    if (t < 0 || t >= form.gallery.length) return;
    const g = [...form.gallery];
    [g[i], g[t]] = [g[t], g[i]];
    set("gallery", g);
  };

  const toggleCat = (cat) =>
    setForm((f) => {
      const has = f.categories.includes(cat);
      const categories = has ? f.categories.filter((x) => x !== cat) : [...f.categories, cat];
      return { ...f, categories, category: categories[0] || "" };
    });

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.categories.length) return toast.error("Pick at least one category");
    setSaving(true);
    const payload = { ...form, category: form.categories[0], order: Number(form.order) || 0 };
    try {
      if (initial?.id) await api.put(`/admin/projects/${initial.id}`, payload);
      else await api.post("/admin/projects", payload);
      toast.success("Project saved");
      onSaved();
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-bg/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto overscroll-contain p-4 md:p-8" data-testid="project-form-modal" data-lenis-prevent>
      <div className="w-full max-w-3xl rounded-xl border border-border bg-surface my-4">
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-surface z-10">
          <h2 className="font-serif text-2xl">{initial?.id ? "Edit project" : "New project"}</h2>
          <button onClick={onClose} data-testid="close-project-form" className="text-muted2 hover:text-ink"><X /></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className={lbl}>Title *</label><input data-testid="pf-title" className={field} value={form.title} onChange={(e) => set("title", e.target.value)} /></div>
            <div><label className={lbl}>Client</label><input data-testid="pf-client" className={field} value={form.client} onChange={(e) => set("client", e.target.value)} /></div>
          </div>
          <div>
            <label className={lbl}>Categories * <span className="text-muted2/60 normal-case">(pick all that apply, shown on the cover & filters)</span></label>
            <div className="flex flex-wrap gap-2" data-testid="pf-categories">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat} type="button" onClick={() => toggleCat(cat)}
                  data-testid={`pf-cat-${cat.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className={`rounded-full px-3 py-1.5 text-xs font-mono uppercase tracking-wide border transition-colors ${
                    form.categories.includes(cat) ? "bg-lime text-bg border-lime" : "border-border text-muted2 hover:text-ink hover:border-ink"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className={lbl}>Year</label><input data-testid="pf-year" className={field} value={form.year} onChange={(e) => set("year", e.target.value)} /></div>
            <div><label className={lbl}>Order</label><input data-testid="pf-order" type="number" className={field} value={form.order} onChange={(e) => set("order", e.target.value)} /></div>
          </div>
          <div><label className={lbl}>Tagline</label><input data-testid="pf-tagline" className={field} value={form.tagline} onChange={(e) => set("tagline", e.target.value)} /></div>

          <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-border p-4 hover:border-accent transition-colors" data-testid="pf-featured-toggle">
            <input type="checkbox" data-testid="pf-featured" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-accent w-4 h-4 mt-0.5" />
            <span>
              <span className="text-ink text-sm block">Feature on homepage</span>
              <span className="text-muted2 text-xs">Featured projects appear in “Selected Work” on the home page. Untick to keep a project on the Work page only.</span>
            </span>
          </label>

          <div>
            <label className={lbl}>Cover image</label>
            <div className="flex items-center gap-4">
              {form.cover && <img src={mediaUrl(form.cover)} alt="cover" className="w-24 h-16 object-cover rounded-lg border border-border" />}
              <input type="text" className={field} value={form.cover} onChange={(e) => set("cover", e.target.value)} placeholder="Paste URL or upload" />
              <input ref={coverRef} type="file" accept="image/*" hidden onChange={onCover} data-testid="pf-cover-upload" />
              <button onClick={() => coverRef.current.click()} className="shrink-0 inline-flex items-center gap-2 rounded-lg border border-border px-4 py-3 text-sm hover:border-accent"><Upload size={16} /> Upload</button>
            </div>
          </div>

          <div><label className={lbl}>The problem</label><textarea rows={3} className={field} value={form.problem} onChange={(e) => set("problem", e.target.value)} /></div>
          <div><label className={lbl}>Our approach</label><textarea rows={3} className={field} value={form.approach} onChange={(e) => set("approach", e.target.value)} /></div>
          <div><label className={lbl}>Deliverables (one per line)</label><textarea rows={3} className={field} value={form.deliverables.join("\n")} onChange={(e) => setList("deliverables", e.target.value)} /></div>
          <div><label className={lbl}>Results / outcomes (one per line)</label><textarea rows={3} className={field} value={form.results.join("\n")} onChange={(e) => setList("results", e.target.value)} /></div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={lbl + " mb-0"}>Gallery (images, videos & PDFs)</label>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-muted2 uppercase tracking-widest">Layout</span>
                <select data-testid="pf-gallery-layout" className="bg-transparent border border-border rounded-lg px-2 py-1 text-ink"
                  value={form.gallery_layout} onChange={(e) => set("gallery_layout", e.target.value)}>
                  <option value="grid" className="bg-surface">Grid (side by side)</option>
                  <option value="grid2" className="bg-surface">Grid, 2 columns (best on mobile)</option>
                  <option value="stack" className="bg-surface">Stacked (full width)</option>
                  <option value="feed" className="bg-surface">Feed (Instagram grids)</option>
                </select>
              </div>
            </div>
            <p className="text-muted2 text-xs mb-3">Use ‹ › to reorder, order here is exactly how they appear on the case study.</p>
            <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mb-3">
              {form.gallery.map((m, i) => (
                <div key={i} className="space-y-1">
                  <div className="relative group rounded-lg overflow-hidden border border-border aspect-square bg-bg flex items-center justify-center">
                    {m.type === "video"
                      ? <video src={mediaUrl(m.url)} className="w-full h-full object-cover" />
                      : m.type === "pdf"
                      ? <div className="flex flex-col items-center gap-1 text-muted2 p-2 text-center"><FileText size={22} /><span className="text-[9px] font-mono uppercase">PDF</span></div>
                      : <img src={mediaUrl(m.url)} alt="" className="w-full h-full object-cover" />}
                    <div className="absolute inset-x-0 bottom-0 flex justify-between p-1 opacity-0 group-hover:opacity-100 bg-gradient-to-t from-bg/90 to-transparent">
                      <button onClick={() => moveMedia(i, -1)} disabled={i === 0} className="bg-bg/80 rounded-full p-1 text-ink disabled:opacity-30" data-testid={`gal-left-${i}`}><ChevronLeft size={13} /></button>
                      <button onClick={() => moveMedia(i, 1)} disabled={i === form.gallery.length - 1} className="bg-bg/80 rounded-full p-1 text-ink disabled:opacity-30" data-testid={`gal-right-${i}`}><ChevronRight size={13} /></button>
                    </div>
                    <button onClick={() => removeMedia(i)} className="absolute top-1 right-1 bg-bg/80 rounded-full p-1 text-ink opacity-0 group-hover:opacity-100" data-testid={`gal-remove-${i}`}><Trash2 size={13} /></button>
                  </div>
                  {m.type !== "pdf" && (
                    <input
                      value={m.caption || ""}
                      onChange={(e) => setCaption(i, e.target.value)}
                      placeholder={form.gallery_layout === "feed" ? "@handle" : "Caption"}
                      data-testid={`gal-caption-${i}`}
                      className="w-full bg-transparent border border-border rounded px-2 py-1 text-[11px] text-ink focus:border-accent outline-none"
                    />
                  )}
                  {m.type !== "pdf" && form.gallery_layout !== "feed" && (
                    <input
                      value={m.section || ""}
                      onChange={(e) => setField(i, "section", e.target.value)}
                      placeholder="Section (e.g. Packaging)"
                      data-testid={`gal-section-${i}`}
                      className="w-full bg-transparent border border-border rounded px-2 py-1 text-[11px] text-ink focus:border-accent outline-none"
                    />
                  )}
                  {form.gallery_layout === "feed" && m.type !== "pdf" && (
                    <>
                      <input
                        value={m.brand || ""}
                        onChange={(e) => setField(i, "brand", e.target.value)}
                        placeholder="Brand name"
                        data-testid={`gal-brand-${i}`}
                        className="w-full bg-transparent border border-border rounded px-2 py-1 text-[11px] text-ink focus:border-accent outline-none"
                      />
                      <select
                        value={m.kind || "grid"}
                        onChange={(e) => setField(i, "kind", e.target.value)}
                        data-testid={`gal-kind-${i}`}
                        className="w-full bg-surface border border-border rounded px-2 py-1 text-[11px] text-ink focus:border-accent outline-none"
                      >
                        <option value="grid" className="bg-surface">Grid (feed)</option>
                        <option value="post" className="bg-surface">Post (spotlight)</option>
                        <option value="reel" className="bg-surface">Reel (spotlight)</option>
                      </select>
                    </>
                  )}
                </div>
              ))}
              <button onClick={() => galleryRef.current.click()} className="rounded-lg border border-dashed border-border aspect-square flex items-center justify-center text-muted2 hover:border-accent hover:text-accent" data-testid="pf-gallery-add"><Plus /></button>
            </div>
            <input ref={galleryRef} type="file" accept="image/*,video/*,application/pdf" multiple hidden onChange={onGallery} />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border sticky bottom-0 bg-surface">
          {uploading && <span className="text-muted2 text-sm mr-auto">Uploading…</span>}
          <button onClick={onClose} className="rounded-full border border-border px-6 py-3 text-sm hover:border-ink">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="pf-save" className="rounded-full bg-accent text-bg px-6 py-3 text-sm font-medium hover:bg-accent-hover disabled:opacity-60">{saving ? "Saving…" : "Save project"}</button>
        </div>
      </div>
    </div>
  );
}
